import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, loadStoredUser } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Building2, ChevronDown, Plus, LogOut, Loader2, Check } from "lucide-react";

/**
 * Phase 22 — Persistent tenant switcher.
 *
 * Renders in the top bar of AppShell. Hidden entirely when the identity
 * has exactly one membership AND isn't a platform_admin (the common
 * single-tenant case — no need to add UI chrome).
 */
export default function TenantSwitcher() {
  const navigate = useNavigate();
  const { switchTenant, logout } = useAuth();
  const user = loadStoredUser();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch (_e) {}
  };
  useEffect(() => { load(); }, []);

  const memberships = me?.memberships || [];
  const isPla = !!me?.is_platform_admin;

  // Hide entirely if single-tenant + not a platform_admin.
  if (memberships.length <= 1 && !isPla) return null;

  const current = memberships.find((m) => m.tenant_id === me?.current_tenant_id);

  const pick = async (m) => {
    if (m.tenant_id === me?.current_tenant_id) {
      setOpen(false);
      return;
    }
    setBusy(m.tenant_id);
    try {
      await switchTenant({ tenant_id: m.tenant_id });
      toast.success(`Switched to ${m.tenant_name}`);
      setOpen(false);
      // Soft refresh — bounce to dashboard.
      navigate("/app/dashboard", { replace: true });
      setTimeout(() => window.location.reload(), 60);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
          data-testid="tenant-switcher"
        >
          <div className="grid h-6 w-6 place-items-center rounded-md bg-secondary/10 text-secondary">
            <Building2 className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 text-left">
            <div className="truncate text-sm font-medium text-primary" data-testid="tenant-switcher-name">
              {current?.tenant_name || "…"}
            </div>
            <div className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">
              {(current?.roles || []).join(", ")}
            </div>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition group-hover:translate-y-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="tenant-switcher-menu">
        <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground">
          Workspaces
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {memberships.map((m) => (
            <button
              key={m.tenant_id}
              type="button"
              onClick={() => pick(m)}
              disabled={!!busy}
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent"
              data-testid={`tenant-switcher-item-${m.tenant_slug}`}
            >
              <div className="grid h-6 w-6 place-items-center rounded-md bg-secondary/10 text-secondary">
                <Building2 className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-primary">{m.tenant_name}</div>
                <div className="truncate text-[10px] uppercase tracking-widest text-muted-foreground">
                  {(m.roles || []).join(", ")}
                </div>
              </div>
              {busy === m.tenant_id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              ) : m.tenant_id === me?.current_tenant_id ? (
                <Check className="h-4 w-4 text-secondary" />
              ) : null}
            </button>
          ))}
          {memberships.length === 0 && (
            <div className="px-3 py-3 text-center text-xs text-muted-foreground">
              You're not on any workspaces yet.
            </div>
          )}
        </div>
        {isPla && (
          <>
            <div className="border-t border-border py-1">
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/app/platform/tenants"); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                data-testid="tenant-switcher-platform-link"
              >
                <Plus className="h-4 w-4 text-secondary" />
                Platform admin console
              </button>
            </div>
            <Badge className="mx-3 my-2 bg-secondary/10 text-secondary" data-testid="tenant-switcher-pla-badge">
              Platform admin
            </Badge>
          </>
        )}
        <div className="border-t border-border py-1">
          <button
            type="button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
            data-testid="tenant-switcher-sign-out"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
